import { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { cx } from '@emotion/css';
import { useTranslation } from 'next-i18next';

import { useClickOutside } from '@phoenix/core/frontend/shared/util/hooks/use-click-outside';
import { PhoenixIcon } from '@phoenix/design-system/frontend/ui/phoenix-icon/phoenix-icon';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { DynamicLinkWrapper } from '@phoenix/team-m/dashboard/frontend/shared/dynamic-link-wrapper';

const arrowRightIcon = getDesignSystemIcon('icon-arrow-right-bold-16.svg');
const exclamationMarkIcon = getDesignSystemIcon('icon-exclamationmark-16.svg');
const closeIcon = getDesignSystemIcon('icon-cross-16.svg');

type InteractiveMenuAction = {
  icon: string;
  text: string;
  routePath: string;
  onClick?: () => void;
};

interface InteractiveMenuButtonProps {
  isActive?: boolean;
  actions: InteractiveMenuAction[];
  className?: string;
  dataTestId?: string;
}

export const InteractiveMenuButton: FC<InteractiveMenuButtonProps> = ({ isActive = false, actions, className, dataTestId }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  useClickOutside(ref, () => setIsOpen(false));

  const onClick = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isActive && setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (isOpen && !isActive) setIsOpen(false);
  }, [isActive]);

  return (
    <div className={cx('flex flex-col items-end', className)} ref={ref}>
      <button
        onClick={onClick}
        className="relative flex h-[38px] w-[38px] items-center justify-center rounded-full focus-visible:outline-primary-black"
        data-testid={dataTestId}
        aria-hidden={isActive ? 'false' : 'true'}
        tabIndex={isActive ? 0 : -1}
        aria-label={t('dashboard_productStage_hint_actionsLabel') as string}
      >
        {isActive && !isOpen && (
          <span className="absolute inline-flex h-6 w-6 animate-[ping_1.5s_ease-in-out_infinite] rounded-full bg-primary-orange" />
        )}
        <span className="relative inline-flex h-8 w-8 rounded-full bg-primary-orange p-2">
          <PhoenixIcon icon={isOpen ? closeIcon : exclamationMarkIcon} />
        </span>
      </button>
      {isOpen && (
        <div className="mt-3 flex animate-fade-in flex-col items-end space-y-2" data-testid="interactive-menu-button_actions">
          {actions.map((action: InteractiveMenuAction) => (
            <DynamicLinkWrapper
              routePath={action.routePath}
              key={action.text}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick?.();
              }}
            >
              <div className="flex items-center rounded-lg bg-primary-black px-4 py-3 text-primary-white">
                <PhoenixIcon icon={action.icon} />
                <span className="ml-[10px] stihl-text-body-sm-bold">{action.text}</span>
                <PhoenixIcon icon={arrowRightIcon} className="ml-3 h-3 w-3" />
              </div>
            </DynamicLinkWrapper>
          ))}
        </div>
      )}
    </div>
  );
};
